import {Reducer, updateFactory} from '@modifi/redux'
import {
  COMPANY_OWNERSHIP_STRUCTURE,
  aUserFeedBackSubmitFailed,
  aUserFeedBackSubmitSuccessful,
  COMPANY_REVENUE_ANNUAL,
  COMPANY_REVENUE_EXPORTS,
  COMPANY_REVENUE_GROUP,
} from '@modifi/plugin-onboarding'
import {OwnershipStructure} from '@modifi/enums'
import {aPush} from '@modifi/router'

import {
  SECTION_PRE_UNDERWRITING_COMPANY_FINANCIALS,
  PRE_UNDERWRITING_BUYER_DETAILS,
} from '../constants'
import {ROUTE_INTRO} from '../../../../routes/constants'

import {
  aFetchCompany,
  aUpdateCompany,
  selectCompanyExternalId,
  selectOnboardingCompany,
  mapCompanyToRequest,
} from '../../../../data/company'
import {getLogger} from '../../../../utils'
import {aFetchInputFields} from '../../../../data/input-fields'

const logger = getLogger('pre-underwriting-company-financials-page')

export const selectInitialFormData = (
  state: Record<string, any>
): {initialValues: Record<string, any>; defaultValues: Record<string, any>} => {
  const defaultValues = {
    [COMPANY_OWNERSHIP_STRUCTURE]: OwnershipStructure.SINGLE_ENTITY,
  }

  const company = selectOnboardingCompany(state)
  const initialValues = {}
  if (company) {
    const {
      annualRevenueCategoryCode,
      annualRevenueExportPercentage,
      ownershipStructure,
      annualRevenueGroupCategoryCode,
    } = company

    Object.assign(initialValues, {
      [COMPANY_REVENUE_ANNUAL]: annualRevenueCategoryCode,
      [COMPANY_REVENUE_EXPORTS]: annualRevenueExportPercentage,
      [COMPANY_OWNERSHIP_STRUCTURE]: ownershipStructure,
    })

    if (ownershipStructure && ownershipStructure !== OwnershipStructure.SINGLE_ENTITY) {
      Object.assign(initialValues, {
        [COMPANY_REVENUE_GROUP]: annualRevenueGroupCategoryCode,
      })
    }
  }

  return {
    initialValues,
    defaultValues,
  }
}

const {actions, selectors, reducer} = updateFactory({
  actionNamePrefix: 'pages/company-financials/submit',
  rootSelector: state => state?.pages?.preUnderwriting?.companyFinancials,
  submitRequest:
    (a, changes: Record<string, any>) =>
    async (dispatch, getState): Promise<void> => {
      const currentCompanyId = selectCompanyExternalId(getState())
      logger.info(
        'Submitting pre-underwriting company financial details for company=%s',
        currentCompanyId
      )
      logger.debug('changes: %o', changes)

      dispatch(a.pendingAction())

      try {
        await dispatch(aUpdateCompany(mapCompanyToRequest(changes)))
      } catch (e: any) {
        dispatch(a.errorAction(e))
        dispatch(aUserFeedBackSubmitFailed(SECTION_PRE_UNDERWRITING_COMPANY_FINANCIALS))
        return
      }
      dispatch(a.successAction())
      dispatch(aUserFeedBackSubmitSuccessful(SECTION_PRE_UNDERWRITING_COMPANY_FINANCIALS))
      await aFetchCompany()
      dispatch(aFetchInputFields())

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'preonboarding_company_financials_submit',
      })

      dispatch(aPush(ROUTE_INTRO, {page: PRE_UNDERWRITING_BUYER_DETAILS}))
    },
})

export const {aSubmit: aSavePage, aReset} = actions

export default reducer as Reducer

export const {
  selectUpdatePending: selectSavePending,

  selectUpdateErrorMessage: selectSaveErrorMessage,
} = selectors
